import { BUILDER_INTERFACE_LOCAL_STORAGE_KEYS } from '@/constants/localStorage';
import { BuilderState } from '@/contexts/BuilderContext';
import { toast } from 'sonner';

// utils/storage.ts
const STORAGE_KEY = BUILDER_INTERFACE_LOCAL_STORAGE_KEYS;
const MAX_HISTORY_SIZE = 10; // Limit undo/redo history to prevent quota issues

// Helper to check localStorage usage
const getStorageSize = (): number => {
  let total = 0;
  for (const key in localStorage) {
    if (Object.prototype.hasOwnProperty.call(localStorage, key)) {
      total += localStorage[key].length + key.length;
    }
  }
  return total;
};

// Flush the storage
export const flushStorage = (): void => {
  localStorage.clear();
};

// clean up old storage entries
export const cleanUpOldStorageEntires = (): void => {
  try {
    const keys = Object.keys(localStorage);
    const builderKeys = keys.filter((key) => key.startsWith(STORAGE_KEY));
    builderKeys.forEach((key) => {
      localStorage.removeItem(key);
    });
  } catch (err) {
    console.warn('Failed to clean up old storage entries', err);
  }
};

// Helper to clean up old storage entries
const cleanupOldStorage = (): void => {
  try {
    const keys = Object.keys(localStorage);
    const builderKeys = keys.filter((key) => key.startsWith(STORAGE_KEY));

    // Keep only the most recent 20 builder states
    if (builderKeys.length > 20) {
      const keysToRemove = builderKeys.slice(0, builderKeys.length - 20);
      keysToRemove.forEach((key) => {
        console.log(`🧹 Cleaning up old storage: ${key}`);
        localStorage.removeItem(key);
      });
    }
  } catch (err) {
    console.warn('Failed to cleanup old storage', err);
  }
};

// Helper to optimize state before saving
const optimizeStateForStorage = (state: BuilderState): BuilderState => {
  return {
    ...state,
    // Limit history size to prevent quota issues
    history: {
      past: state.history.past.slice(-MAX_HISTORY_SIZE),
      future: state.history.future.slice(0, MAX_HISTORY_SIZE),
    },
    // Remove temporary UI state that doesn't need persistence
    selectedElementId: null,
    isDragging: false,
  };
};

export const loadState = (landingPageID: string): BuilderState | undefined => {
  try {
    const storageKEY = `${STORAGE_KEY}-${landingPageID}`;
    const serializedState = localStorage.getItem(storageKEY);
    if (serializedState === null) return undefined;
    return JSON.parse(serializedState);
  } catch (err) {
    console.warn('Failed to load state from localStorage', err);
    return undefined;
  }
};

// Cache for preventing duplicate saves
let lastSavedState: string | null = null;
let lastSavedKey: string | null = null;

export const saveState = (landingPageID: string, state: BuilderState) => {
  try {
    // Optimize state before saving
    const optimizedState = optimizeStateForStorage(state);
    const serializedState = JSON.stringify(optimizedState);
    const storageKEY = `${STORAGE_KEY}-${landingPageID}`;

    // Skip save if state hasn't changed
    if (lastSavedState === serializedState && lastSavedKey === storageKEY) {
      return;
    }

    // Check storage size before saving (only if state actually changed)
    const currentSize = getStorageSize();
    const newDataSize = serializedState.length + storageKEY.length;

    // If approaching quota (estimate 5MB = 5,000,000 chars), cleanup first
    if (currentSize + newDataSize > 4000000) {
      console.warn(
        '⚠️ localStorage approaching quota, cleaning up...',
        currentSize + newDataSize
      );
      cleanupOldStorage();
    }

    localStorage.setItem(storageKEY, serializedState);

    // Update cache
    lastSavedState = serializedState;
    lastSavedKey = storageKEY;

    // Log storage usage for debugging (less frequently)
    if (currentSize > 3000000 && Math.random() < 0.1) {
      // Only log 10% of the time
      console.warn(
        `📊 localStorage usage: ${Math.round(currentSize / 1000)}KB`
      );
    }
  } catch (err) {
    if (err.name === 'QuotaExceededError') {
      console.error('❌ localStorage quota exceeded! Attempting cleanup...');

      try {
        // Emergency cleanup - remove old builder states
        cleanupOldStorage();

        // Try to save again with more aggressive optimization
        const emergencyState = {
          ...optimizeStateForStorage(state),
          // Remove history entirely in emergency
          history: { past: [], future: [] },
        };

        const emergencyData = JSON.stringify(emergencyState);
        localStorage.setItem(`${STORAGE_KEY}-${landingPageID}`, emergencyData);

        console.log('✅ Emergency save successful (history cleared)');

        // Show user-friendly message
        if (typeof window !== 'undefined') {
          toast.warning(
            'Storage space low! Your work is saved but undo history was cleared to free up space. Consider exporting your work.'
          );
        }
      } catch (emergencyErr) {
        console.error('❌ Emergency save failed:', emergencyErr);

        // Show critical error to user
        if (typeof window !== 'undefined') {
          toast.error(
            'Critical: Unable to save your work! Please export/backup your current work immediately.'
          );
        }
      }
    } else {
      console.warn('Failed to save state to localStorage', err);
    }
  }
};
